import { combineEpics, ofType } from 'redux-observable'
import { from, of } from 'rxjs'
import { catchError, switchMap, mergeMap } from 'rxjs/operators'
import join from 'url-join'
import dayjs from 'dayjs'
import { getAppConfig } from 'app-config'
import { getAuthClient } from 'auth'
import { resilientFetch } from 'utils/resilientRequest'
import * as actions from './actions'
import * as types from './actions/types'
import { DOWNLOAD_COLUMNS } from './components/helpers'

export const mapVehicleTypes = (vehicleTypes) =>
  (vehicleTypes || []).map(({ key, label }) => ({
    key,
    label: label || key,
  }))


// No longer needed: all params go in POST body except fleetId (for backend compatibility)

const fetchVehicleEmissionsEffect = (action$) =>
  action$.pipe(
    ofType(types.FETCH_REPORT),
    switchMap(({ payload }) => {
      if (!payload?.fleetIds?.length) {
        return of(actions.fetchReportError('Please select at least one fleet.'))
      }

      const appConfig = getAppConfig()
      const baseUrl = join(appConfig.app.api, '/report/vehicleemissionsreport')
      const fleetIdParam = payload.fleetIds.join(',')
      const requestUrl = `${baseUrl}?fleetId=${encodeURIComponent(fleetIdParam)}`

      const request$ = from((async () => {
        const authClient = getAuthClient()
        const token = await authClient.getTokenSilently()
        const response = await resilientFetch(requestUrl, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        })

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`)
        }

        const payloadJson = await response.json()
        const item = payloadJson?.item || payloadJson?.data || payloadJson // fallback to root for new API
        const message = payloadJson?.response?.message || payloadJson?.apiResponse?.message || ''
        const success = payloadJson?.response?.success ?? payloadJson?.apiResponse?.success

        if (success === false && !message) {
          throw new Error('Unable to load emissions data.')
        }

        return actions.fetchReportFulfilled({
          report: item,
          infoMessage: success === false ? '' : message,
          receivedAt: new Date().toISOString(),
        })
      })())

      return request$.pipe(
        catchError((error) => of(actions.fetchReportError(error?.message || 'Unable to load emissions data.')))
      )
    })
  )

const downloadVehicleEmissionsEffect = (action$) =>
  action$.pipe(
    ofType(types.REQUEST_DOWNLOAD),
    mergeMap(({ payload }) => {
      const { fleetIds, filters, downloadAll, title } = payload

      if (!fleetIds?.length) {
        return of(actions.requestDownloadError('Please select at least one fleet.'))
      }

      const appConfig = getAppConfig()

      const request$ = from((async () => {
        const authClient = getAuthClient()
        const token = await authClient.getTokenSilently()

        // Build query parameters for GET request
        const params = new URLSearchParams()
        params.append('fleetId', fleetIds.join(','))
        DOWNLOAD_COLUMNS.forEach(col => params.append('columns', col))

        // Only apply filters if not downloading all
        if (!downloadAll && filters) {
          if (filters.periods?.length) {
            filters.periods.forEach((period, index) => {
              params.append(`periods[${index}].fromMonth`, dayjs(period.fromMonth).startOf('month')
                .format('YYYY-MM-DD'))
              params.append(`periods[${index}].toMonth`, dayjs(period.toMonth).endOf('month')
                .format('YYYY-MM-DD'))
            })
          } else {
            if (filters.fromMonth) {
              params.append('fromMonth', dayjs(filters.fromMonth).startOf('month')
                .format('YYYY-MM-DD'))
            }
            if (filters.toMonth) {
              params.append('toMonth', dayjs(filters.toMonth).endOf('month')
                .format('YYYY-MM-DD'))
            }
          }

          if (filters.vehicleTypes?.length) {
            filters.vehicleTypes.forEach(vt => params.append('vehicleTypes', vt))
          }
        }

        const requestUrl = `${join(appConfig.app.api, '/download/vehicleemissions')}?${params.toString()}`

        const response = await resilientFetch(requestUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error(`Download failed with status ${response.status}`)
        }

        const blob = await response.blob()
        const fileName = `${title || 'vehicle-emissions'}-${downloadAll ? 'all-' : ''}${dayjs().format('YYYY-MM-DD-HHmm')}.xlsx`
        const objectUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(objectUrl)

        return actions.requestDownloadFulfilled()
      })())

      return request$.pipe(
        catchError((error) => of(actions.requestDownloadError(error?.message || 'Unable to download emissions report.')))
      )
    })
  )

export const effects = combineEpics(fetchVehicleEmissionsEffect, downloadVehicleEmissionsEffect)

export default effects
